import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell, { SectionCard, StatCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Laptop, DeviceMobile, Monitor, Keyboard, Printer, Code, GlobeHemisphereWest,
  ShieldCheck, Wrench, MagnifyingGlass, ShoppingBag, Lightning, Clock, CheckCircle, Sparkle,
  Plus, ArrowRight, Truck, GearSix, Package,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const err = (e) => toast.error(formatApiError(e?.response?.data?.detail));

const CATEGORY_META = {
  laptop:     { label: "Laptops",      icon: Laptop,     color: "bg-zinc-900 text-white" },
  desktop:    { label: "Desktops",     icon: GearSix,    color: "bg-slate-800 text-white" },
  monitor:    { label: "Monitors",     icon: Monitor,    color: "bg-blue-700 text-white" },
  accessory:  { label: "Accessories",  icon: Keyboard,   color: "bg-purple-700 text-white" },
  printer:    { label: "Printers",     icon: Printer,    color: "bg-amber-700 text-white" },
  software:   { label: "Software",     icon: Code,       color: "bg-emerald-700 text-white" },
  networking: { label: "Networking",   icon: GlobeHemisphereWest, color: "bg-cyan-700 text-white" },
  amc_plan:   { label: "AMC Plans",    icon: ShieldCheck, color: "bg-rose-700 text-white" },
  service:    { label: "Services",     icon: Wrench,     color: "bg-orange-700 text-white" },
};

const STATUS_META = {
  draft:            { label: "Draft",            color: "bg-zinc-100 text-zinc-700" },
  submitted:        { label: "Submitted",        color: "bg-blue-100 text-blue-700" },
  pending_approval: { label: "Pending approval", color: "bg-amber-100 text-amber-800" },
  approved:         { label: "Approved",         color: "bg-emerald-100 text-emerald-700" },
  rejected:         { label: "Rejected",         color: "bg-rose-100 text-rose-700" },
  with_partner:     { label: "With TheGoodMen",  color: "bg-teal-100 text-teal-700" },
  in_progress:      { label: "In progress",      color: "bg-cyan-100 text-cyan-700" },
  shipped:          { label: "Shipped",          color: "bg-indigo-100 text-indigo-700" },
  delivered:        { label: "Delivered",        color: "bg-emerald-200 text-emerald-800" },
  resolved:         { label: "Resolved",         color: "bg-emerald-200 text-emerald-800" },
  cancelled:        { label: "Cancelled",        color: "bg-zinc-200 text-zinc-600" },
};

const INR = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;

export default function TechDesk() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "shop";
  const setTab = (t) => setParams({ tab: t });
  return (
    <AppShell title="TechDesk">
      <div className="border-b border-zinc-200 mb-5 flex flex-wrap gap-1" data-testid="tech-tabs">
        {[
          ["shop", "Shop", ShoppingBag],
          ["service", "Need help", Wrench],
          ["requests", "My requests", Package],
        ].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 transition ${
              tab === k ? "border-emerald-600 text-emerald-700" : "border-transparent text-zinc-600 hover:text-zinc-900"
            }`}>
            <Icon size={16}/> {label}
          </button>
        ))}
      </div>
      {tab === "shop"     && <Shop/>}
      {tab === "service"  && <Service/>}
      {tab === "requests" && <MyRequests/>}
      <PoweredByTGM/>
    </AppShell>
  );
}

function PoweredByTGM() {
  return (
    <div className="mt-8 pt-5 border-t border-zinc-200 flex items-center justify-between text-xs text-zinc-500" data-testid="powered-by-tgm">
      <span>Fulfilled & supported by <a href="https://thegoodmen.in" target="_blank" rel="noreferrer" className="font-semibold text-teal-700 hover:underline">TheGoodMen</a> · India's IT service backbone</span>
      <span className="font-mono">SLA-backed · AMC available · 2-day delivery in metros</span>
    </div>
  );
}

// ─── Shop ──────────────────────────────────────────────────────────
function Shop() {
  const [data, setData] = useState({ items: [], categories: [] });
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = async () => {
    try { const r = await api.get(`/tech/catalog`); setData(r.data); } catch (e) { err(e); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return data.items.filter(it => {
      if (cat !== "all" && it.category !== cat) return false;
      if (q && !`${it.name} ${it.brand} ${it.model || ""} ${(it.tags || []).join(" ")}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [data.items, cat, q]);

  return (
    <div data-testid="shop-tab">
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-teal-700 via-emerald-700 to-emerald-800 rounded-xl p-6 mb-6 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px)", backgroundSize: "32px 32px" }}/>
        <div className="relative">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-200 mb-1">Powered by TheGoodMen</div>
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Your office IT, made effortless.</h2>
          <p className="text-emerald-50 text-sm max-w-xl">Browse laptops, monitors, software, networking gear and AMC plans — pre-negotiated wholesale prices, on-site delivery, AMC opt-in. No quotes, no paperwork.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <span className="text-xs bg-white/10 backdrop-blur px-3 py-1 rounded-full">⚡ Same-day quotes</span>
            <span className="text-xs bg-white/10 backdrop-blur px-3 py-1 rounded-full">🚚 Pan-India delivery</span>
            <span className="text-xs bg-white/10 backdrop-blur px-3 py-1 rounded-full">🛡️ Optional AMC</span>
            <span className="text-xs bg-white/10 backdrop-blur px-3 py-1 rounded-full">📋 Auto-PO & approvals</span>
          </div>
        </div>
      </div>

      {/* Search + categories */}
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-3 text-zinc-400"/>
          <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search MacBook, monitor, Microsoft 365…" className="pl-9 h-10" data-testid="shop-search"/>
        </div>
        <div className="flex gap-1.5 flex-wrap" data-testid="cat-pills">
          <CatPill k="all" label={`All · ${data.items.length}`} active={cat==="all"} onClick={() => setCat("all")} />
          {data.categories.map(c => (
            <CatPill key={c.key} k={c.key} label={`${CATEGORY_META[c.key]?.label || c.key} · ${c.count}`}
                     active={cat===c.key} onClick={() => setCat(c.key)} />
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="catalog-grid">
        {filtered.map(it => <ProductCard key={it.id} item={it} onClick={() => setOpen(it)}/>)}
        {filtered.length === 0 && <div className="col-span-full text-center py-12 text-sm text-zinc-500">No items match these filters.</div>}
      </div>

      {open && <ProductDialog item={open} onClose={() => setOpen(null)} onRequested={load} />}
    </div>
  );
}

function CatPill({ k, label, active, onClick }) {
  const meta = CATEGORY_META[k] || {};
  const Icon = meta.icon;
  return (
    <button onClick={onClick} data-testid={`cat-${k}`}
      className={`text-xs px-3 py-1.5 rounded-full border transition flex items-center gap-1.5 ${
        active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"
      }`}>
      {Icon && <Icon size={13}/>} {label}
    </button>
  );
}

function ProductCard({ item, onClick }) {
  const m = CATEGORY_META[item.category] || {};
  return (
    <button onClick={onClick} data-testid={`product-${item.id}`}
      className="group bg-white border border-zinc-200 rounded-xl overflow-hidden text-left hover:border-emerald-400 hover:shadow-lg transition-all flex flex-col">
      <div className="relative aspect-square bg-zinc-50 overflow-hidden">
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} loading="lazy"
               className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${m.color}`}>
            {m.icon && <m.icon size={56}/>}
          </div>
        )}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {m.label && <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${m.color}`}>{m.label}</span>}
          {!item.in_stock && <span className="text-[10px] bg-zinc-900 text-white px-2 py-0.5 rounded">Out of stock</span>}
        </div>
        {item.sla_days <= 3 && (
          <div className="absolute top-2 right-2 text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-200 font-semibold px-2 py-0.5 rounded flex items-center gap-1">
            <Lightning size={10} weight="fill"/>{item.sla_days}d
          </div>
        )}
      </div>
      <div className="p-3.5 flex-1 flex flex-col">
        <div className="text-[11px] font-medium text-zinc-500 mb-0.5">{item.brand || "—"}</div>
        <div className="font-semibold text-sm text-zinc-900 line-clamp-2 leading-snug">{item.name}</div>
        <div className="mt-1 flex flex-wrap gap-1 text-[10px] text-zinc-500">
          {(item.spec_highlights || []).slice(0, 2).map((s, i) => (
            <span key={i} className="px-1.5 py-0.5 bg-zinc-100 rounded">{s}</span>
          ))}
        </div>
        <div className="mt-auto pt-3 flex items-end justify-between">
          <div>
            <div className="text-lg font-bold text-zinc-900 tabular-nums">{INR(item.price)}</div>
            <div className="text-[10px] text-zinc-500">{item.warranty_months ? `${item.warranty_months}m warranty` : "—"}</div>
          </div>
          <div className="text-xs px-2.5 py-1.5 rounded-full bg-emerald-600 text-white font-medium group-hover:bg-emerald-700 transition">
            Request →
          </div>
        </div>
      </div>
    </button>
  );
}

function ProductDialog({ item, onClose, onRequested }) {
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/tech/requests", {
        request_type: item.category === "service" ? "service" : (item.category === "amc_plan" ? "amc" : "purchase"),
        catalog_item_id: item.id,
        qty, title: item.name,
        description: `${item.brand || ""} ${item.name}${qty>1?` × ${qty}`:""}`,
        urgency: "medium",
      });
      toast.success("Request submitted — your IT admin will review it.");
      onClose(); onRequested?.();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const m = CATEGORY_META[item.category] || {};
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl p-0 overflow-hidden" data-testid="product-dialog">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="aspect-square md:aspect-auto bg-zinc-50">
            {item.image_url ? (
              <img src={item.image_url} alt={item.name} className="w-full h-full object-cover"/>
            ) : (
              <div className={`w-full h-full flex items-center justify-center ${m.color}`}>
                {m.icon && <m.icon size={120}/>}
              </div>
            )}
          </div>
          <div className="p-6 flex flex-col">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">{item.brand} · {m.label}</div>
            <h3 className="text-xl font-bold mb-2 leading-snug">{item.name}</h3>
            <div className="text-sm text-zinc-600 mb-3">{item.description}</div>
            <ul className="space-y-1.5 text-xs text-zinc-700 mb-4">
              {(item.spec_highlights || []).map((s, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-emerald-600 mt-0.5 shrink-0"/>{s}</li>
              ))}
            </ul>
            <div className="flex items-center gap-3 mb-4 text-xs">
              <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 rounded flex items-center gap-1">
                <Lightning size={12} weight="fill"/> SLA {item.sla_days}d
              </span>
              {item.warranty_months > 0 && (
                <span className="bg-zinc-100 text-zinc-700 border border-zinc-200 px-2 py-1 rounded flex items-center gap-1">
                  <ShieldCheck size={12} weight="fill"/> {item.warranty_months}m warranty
                </span>
              )}
            </div>
            <div className="mt-auto pt-4 border-t border-zinc-100">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-zinc-500">Pre-negotiated by TheGoodMen</div>
                <div className="text-2xl font-bold tabular-nums">{INR(item.price)}</div>
              </div>
              {item.category !== "service" && item.category !== "amc_plan" && (
                <div className="flex items-center gap-3 mb-3">
                  <Label className="text-xs">Qty</Label>
                  <Input type="number" min={1} max={20} value={qty} onChange={e=>setQty(Math.max(1, Number(e.target.value)))} className="w-20 h-8" data-testid="qty-input"/>
                  <div className="text-xs text-zinc-500">Total: <span className="font-semibold text-zinc-900">{INR(item.price * qty)}</span></div>
                </div>
              )}
              <Button onClick={submit} disabled={busy} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-request">
                <Sparkle size={16} weight="fill"/>{busy ? "Submitting…" : "Request now"}
              </Button>
              <div className="text-[10px] text-zinc-500 mt-2 text-center">Requests under ₹25k for services auto-approve. Hardware over ₹25k routes through IT admin.</div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Service tab (incidents / repairs / questions) ─────────────────
function Service() {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", urgency: "medium",
    asset_label: "", location_label: "", request_type: "service",
  });
  const submit = async () => {
    if (!form.title || !form.description) { toast.error("Please fill title and description"); return; }
    setBusy(true);
    try {
      await api.post("/tech/requests", form);
      toast.success("Service request raised — TheGoodMen has been notified.");
      setForm({ title:"", description:"", urgency:"medium", asset_label:"", location_label:"", request_type:"service" });
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const quickActions = [
    { title: "Laptop slow / overheating", icon: Laptop, color: "bg-blue-600" },
    { title: "Cannot connect to Wi-Fi / VPN", icon: GlobeHemisphereWest, color: "bg-cyan-600" },
    { title: "Printer not working", icon: Printer, color: "bg-amber-600" },
    { title: "Need software license", icon: Code, color: "bg-emerald-600" },
    { title: "Need a new accessory", icon: Keyboard, color: "bg-purple-600" },
    { title: "AMC service visit", icon: Wrench, color: "bg-orange-600" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="service-tab">
      <div className="lg:col-span-2">
        <SectionCard title="Tell us what's wrong" subtitle="Pick a common issue or describe it yourself. TheGoodMen will reach out with the next step.">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-5">
            {quickActions.map(q => (
              <button key={q.title} onClick={() => setForm(f => ({ ...f, title: q.title }))}
                data-testid={`quick-${q.title}`}
                className={`p-3 rounded-lg border text-left transition ${
                  form.title === q.title ? "border-emerald-400 bg-emerald-50" : "border-zinc-200 bg-white hover:border-zinc-400"
                }`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 text-white ${q.color}`}>
                  <q.icon size={18} weight="fill"/>
                </div>
                <div className="text-xs font-medium">{q.title}</div>
              </button>
            ))}
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={form.title} onChange={e=>setForm({...form, title: e.target.value})} placeholder="e.g. Loud fan noise on my laptop" data-testid="svc-title"/>
            </div>
            <div>
              <Label className="text-xs">Describe the issue</Label>
              <Textarea rows={4} value={form.description} onChange={e=>setForm({...form, description: e.target.value})}
                placeholder="When did it start? Anything that triggers it? What you've tried so far…"
                data-testid="svc-desc"/>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Urgency</Label>
                <Select value={form.urgency} onValueChange={v => setForm({...form, urgency: v})}>
                  <SelectTrigger data-testid="svc-urgency"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical · work-blocking</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Asset (optional)</Label>
                <Input value={form.asset_label} onChange={e=>setForm({...form, asset_label: e.target.value})} placeholder="e.g. ACME-LAP-042" data-testid="svc-asset"/>
              </div>
              <div>
                <Label className="text-xs">Location</Label>
                <Input value={form.location_label} onChange={e=>setForm({...form, location_label: e.target.value})} placeholder="e.g. Bengaluru HQ — 3rd floor" data-testid="svc-loc"/>
              </div>
            </div>
            <Button onClick={submit} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="svc-submit">
              <Sparkle size={16} weight="fill"/>{busy ? "Submitting…" : "Raise request"}
            </Button>
          </div>
        </SectionCard>
      </div>
      <div>
        <SectionCard title="How it works">
          <ol className="space-y-3 text-sm">
            {[
              ["You raise this request", "30 seconds, no email chains"],
              ["Auto-approved if under ₹25k", "Hardware buys route to IT admin"],
              ["TheGoodMen takes it from there", "Ticket auto-pushed to their dispatch"],
              ["Status visible in 'My requests'", "ETA · technician · live updates"],
            ].map(([t, sub], i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">{i+1}</span>
                <div>
                  <div className="font-medium text-sm">{t}</div>
                  <div className="text-xs text-zinc-500">{sub}</div>
                </div>
              </li>
            ))}
          </ol>
        </SectionCard>
      </div>
    </div>
  );
}

// ─── My requests timeline ──────────────────────────────────────────
function MyRequests() {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { const r = await api.get(`/tech/requests?mine_only=true`); setRows(r.data); } catch (e) { err(e); }
  };
  useEffect(() => { load(); }, []);
  if (rows.length === 0) {
    return <div className="text-center py-16 text-sm text-zinc-500" data-testid="no-requests">No requests yet. Browse the shop or raise a service request.</div>;
  }
  return (
    <div className="space-y-3" data-testid="my-requests">
      {rows.map(r => <RequestRow key={r.id} r={r}/>)}
    </div>
  );
}

function RequestRow({ r }) {
  const [open, setOpen] = useState(false);
  const s = STATUS_META[r.status] || { label: r.status, color: "bg-zinc-100" };
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`req-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-sm">{r.title}</span>
            <Badge className={s.color}>{s.label}</Badge>
            {r.osticket_number && <span className="text-[10px] font-mono bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded">TGM #{r.osticket_number}</span>}
            <Badge variant="outline" className="text-[10px] capitalize">{r.request_type}</Badge>
          </div>
          <div className="text-xs text-zinc-600">
            {r.catalog_item_name && <span>{r.catalog_item_name}{r.qty > 1 ? ` × ${r.qty}` : ""} · </span>}
            {r.total_amount > 0 && <span className="font-medium">{INR(r.total_amount)} · </span>}
            <span>{new Date(r.created_at).toLocaleString()}</span>
          </div>
          {r.partner_technician && (
            <div className="text-xs text-emerald-700 mt-1">Technician: <span className="font-medium">{r.partner_technician}</span>{r.partner_eta && <> · ETA: <span className="font-medium">{r.partner_eta}</span></>}</div>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`view-${r.id}`}>View</Button>
      </div>
      {open && <RequestDetailDialog r={r} onClose={() => setOpen(false)}/>}
    </div>
  );
}

function RequestDetailDialog({ r, onClose }) {
  const events = [
    r.submitted_at && { ts: r.submitted_at, label: "Submitted by you", color: "bg-zinc-500" },
    r.approved_at && { ts: r.approved_at, label: "Approved", color: "bg-emerald-500" },
    r.last_sync_at && { ts: r.last_sync_at, label: `Received by TheGoodMen${r.osticket_number ? ` (Ticket #${r.osticket_number})` : ""}`, color: "bg-teal-500" },
    ...(r.partner_notes || []).map(n => ({ ts: n.at, label: `${n.by || "TGM"}: ${n.text}`, color: "bg-cyan-500" })),
    r.completed_at && { ts: r.completed_at, label: "Resolved / Delivered", color: "bg-emerald-700" },
  ].filter(Boolean).sort((a,b) => (a.ts > b.ts ? 1 : -1));

  const s = STATUS_META[r.status] || {};
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="req-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {r.title} <Badge className={s.color}>{s.label}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 text-sm">
          {r.description && <div><Label className="text-xs">Description</Label><div className="mt-1 text-zinc-700 whitespace-pre-wrap">{r.description}</div></div>}
          {r.catalog_item_name && (
            <div className="bg-zinc-50 border border-zinc-200 rounded p-3">
              <div className="text-xs text-zinc-500">Item</div>
              <div className="font-medium">{r.catalog_item_name} {r.qty > 1 ? `× ${r.qty}` : ""}</div>
              {r.total_amount > 0 && <div className="text-sm tabular-nums mt-1">Total: <strong>{INR(r.total_amount)}</strong></div>}
            </div>
          )}
          <div>
            <Label className="text-xs">Timeline</Label>
            <div className="mt-2 space-y-2.5 border-l border-zinc-200 pl-4 ml-2">
              {events.map((e, i) => (
                <div key={i} className="relative">
                  <span className={`absolute -left-[22px] w-3 h-3 rounded-full ${e.color} mt-1.5 ring-2 ring-white`}/>
                  <div className="text-xs text-zinc-500">{new Date(e.ts).toLocaleString()}</div>
                  <div className="text-sm">{e.label}</div>
                </div>
              ))}
              {events.length === 0 && <div className="text-xs text-zinc-500">No events yet.</div>}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
